import type React from 'react'
import { createPortal } from 'react-dom'

import { OVERLAY_ROOT_IDS, type OverlayRoot } from './constants'

interface Props {
  root: OverlayRoot
  children?: React.ReactNode
}

const missingRootsWarned = new Set<OverlayRoot>()

/*
 * A missing target means the index.html root div was renamed or dropped out of
 * sync with OVERLAY_ROOT_IDS — the overlay would then vanish silently. Make
 * that loud (once per root, so re-renders don't spam), in dev and prod alike.
 */
const warnMissingRoot = (root: OverlayRoot, id: string): void => {
  if (missingRootsWarned.has(root)) {
    return
  }
  missingRootsWarned.add(root)
  console.error(
    `OverlayPortal: no #${id} element for root "${root}" — overlay content will ` +
      `not render. The overlay roots in index.html must match OVERLAY_ROOT_IDS ` +
      `(src/lib/components/OverlayPortal/constants.ts).`
  )
}

/*
 * Renders floating chrome into one of the ordered overlay roots. Prefer this
 * over `createPortal(..., document.body)` for new modals, confirmations and
 * notifications: the target root orders deterministically against the other
 * roots (see constants.ts), so the content needs no z-index escalation — raw
 * 0–3 inside the root's isolated stacking context is enough.
 * Renders nothing (and logs once) if the root is missing. OCT-4740
 */
export const OverlayPortal = ({ root, children = null }: Props) => {
  const id = OVERLAY_ROOT_IDS[root]
  const target = document.getElementById(id)

  if (!target) {
    warnMissingRoot(root, id)
    return null
  }

  return createPortal(children, target)
}
